import { values, indexBy, prop } from 'ramda';
import moment from 'moment';
import { createSelector } from 'reselect';

import {
  getUsNPPs,
  getUsNppUnavailabilities,
  getUsNuclearOutage,
  getUsNppMonthlyProduction,
  getUsNppPlantLoads,
  getUsNppSnapshot,
} from '../api';
import logger from '../utils/logger';
import { getElapsedHoursInCurrentMonth } from '../utils/timeUtils';

export const US_NPPS_LOAD_ACTION = 'US_NPPS_LOAD_ACTION';
export const US_NPPS_RECEIVE_ACTION = 'US_NPPS_RECEIVE_ACTION';

function loadUsNPPsAction() {
  return { type: US_NPPS_LOAD_ACTION };
}

function receiveUsNPPsAction({
  data,
  errors,
  unavailabilities,
  outage,
  production,
  productionByPlant,
  loads,
  snapshot,
  year,
  plantId,
}) {
  return {
    type: US_NPPS_RECEIVE_ACTION,
    data,
    errors,
    unavailabilities,
    outage,
    production,
    productionByPlant,
    loads,
    snapshot,
    year,
    plantId,
  };
}

export function loadAllUsNPPs() {
  return function dispatchLoadAllUsNPPs(dispatch, getState) {
    const state = getState();
    const alreadyLoaded = usNPPsLoadedSelector(state);
    if (alreadyLoaded) {
      logger.info('[USNPP] loadAllUsNPPs skipped: already loaded');
      return Promise.resolve();
    }
    logger.info('[USNPP] loadAllUsNPPs fetching plant list');
    dispatch(loadUsNPPsAction());
    return getUsNPPs()
      .then(data => {
        logger.info(
          `[USNPP] plant list fetched: ${
            Array.isArray(data) ? data.length : 0
          } items`,
        );
        return dispatch(
          receiveUsNPPsAction({
            data: indexBy(prop('id'), data),
          }),
        );
      })
      .catch(errors => {
        logger.error('[USNPP] Failed to load US NPPs:', errors);
        return dispatch(receiveUsNPPsAction({ errors }));
      });
  };
}

// Helper function to check if data is already loaded
function isDataLoadedForPlant(state, plantId, year, dataType) {
  // For global view (no plantId), check if global data is loaded
  if (!plantId) {
    const globalData = state.usNPPs?.loadedData?.global;
    if (!globalData) return false;

    const yearData = globalData[year];
    if (!yearData) return false;

    return !!yearData[dataType];
  }

  // For plant-specific view
  const plantKey = plantId.toString();
  if (!plantKey || !year) return false;

  const plantData = state.usNPPs?.loadedData?.[plantKey];
  if (!plantData) return false;

  const yearData = plantData[year];
  if (!yearData) return false;

  return !!yearData[dataType];
}

export function loadUsNppUnavailabilities({ year, plantId, start, end } = {}) {
  return function dispatchLoadUsNppUnavailabilities(dispatch, getState) {
    logger.info(
      `[USNPP] loadUsNppUnavailabilities called year=${year} plantId=${plantId ||
        'ALL'}`,
    );
    // Check if data is already loaded
    const state = getState();
    if (isDataLoadedForPlant(state, plantId, year, 'unavailabilities')) {
      logger.info(
        `[USNPP] unavailabilities already loaded for plant ${plantId ||
          'ALL'}, year ${year}`,
      );
      return Promise.resolve();
    }

    dispatch(loadUsNPPsAction());
    return getUsNppUnavailabilities({ year, plantId, start, end })
      .then(data =>
        dispatch(
          receiveUsNPPsAction({
            unavailabilities: data,
            plantId,
            year,
          }),
        ),
      )
      .catch(
        errors =>
          logger.error('Failed to load US NPP unavailabilities:', errors) ||
          dispatch(receiveUsNPPsAction({ errors })),
      );
  };
}

export function loadUsNuclearOutage({ year, plantId } = {}) {
  return function dispatchLoadUsNuclearOutage(dispatch, getState) {
    logger.info(
      `[USNPP] loadUsNuclearOutage called year=${year} plantId=${plantId ||
        'ALL'}`,
    );
    // Check if data is already loaded
    const state = getState();
    if (isDataLoadedForPlant(state, plantId, year, 'outage')) {
      logger.info(
        `[USNPP] outage already loaded for plant ${plantId ||
          'ALL'}, year ${year}`,
      );
      return Promise.resolve();
    }

    dispatch(loadUsNPPsAction());
    return getUsNuclearOutage({ year, plantId })
      .then(data =>
        dispatch(
          receiveUsNPPsAction({
            outage: data,
            plantId,
            year,
          }),
        ),
      )
      .catch(
        errors =>
          logger.error('Failed to load US nuclear outage:', errors) ||
          dispatch(receiveUsNPPsAction({ errors })),
      );
  };
}

export function loadUsNppMonthlyProduction({ year, plantId } = {}) {
  return function dispatchLoadUsNppMonthlyProduction(dispatch, getState) {
    logger.info(
      `[USNPP] loadUsNppMonthlyProduction called year=${year} plantId=${plantId ||
        'ALL'}`,
    );
    // Check if data is already loaded
    const state = getState();
    if (isDataLoadedForPlant(state, plantId, year, 'production')) {
      logger.info(
        `[USNPP] monthly production already loaded for plant ${plantId ||
          'ALL'}, year ${year}`,
      );
      return Promise.resolve();
    }

    dispatch(loadUsNPPsAction());
    return getUsNppMonthlyProduction({ year, plantId })
      .then(data =>
        dispatch(
          receiveUsNPPsAction({
            production: data,
            plantId,
            year,
          }),
        ),
      )
      .catch(
        errors =>
          logger.error('Failed to load US NPP monthly production:', errors) ||
          dispatch(receiveUsNPPsAction({ errors })),
      );
  };
}

// Load plant loads (single aggregated request for all plants for a window)
export function loadUsNppPlantLoadsRange({ start, end } = {}) {
  return function dispatchLoadUsNppPlantLoadsRange(dispatch) {
    logger.info(
      `[USNPP] loadUsNppPlantLoadsRange called start=${start} end=${end}`,
    );
    return getUsNppPlantLoads({ start, end })
      .then(list => {
        const mapped = list.reduce((acc, it) => {
          acc[String(it.plantId)] = {
            // Ensure we use last month's load for pins and popup
            loadRate:
              typeof it.monthlyLoadRate === 'number'
                ? it.monthlyLoadRate
                : typeof it.loadRate === 'number'
                ? it.loadRate
                : 0,
            monthlyLoadRate:
              typeof it.monthlyLoadRate === 'number'
                ? it.monthlyLoadRate
                : null,
            dailyLoadRate:
              typeof it.dailyLoadRate === 'number' ? it.dailyLoadRate : null,
            notif: it.notif,
            month: it.month,
            monthProdMWh: it.monthProdMWh,
            hoursInMonth: it.hoursInMonth,
          };
          return acc;
        }, {});
        return dispatch(receiveUsNPPsAction({ loads: mapped }));
      })
      .catch(errors =>
        logger.error('Failed to load US NPP plant loads:', errors),
      );
  };
}

// Load global snapshot (French-style counts)
export function loadUsNppSnapshot({ date } = {}) {
  return function dispatchLoadUsNppSnapshot(dispatch) {
    logger.info(`[USNPP] loadUsNppSnapshot called date=${date}`);
    return getUsNppSnapshot({ date })
      .then(data => dispatch(receiveUsNPPsAction({ snapshot: data })))
      .catch(errors => logger.error('Failed to load US NPP snapshot:', errors));
  };
}

// Load entire year's global datasets once and build monthly loads per plant
export function loadUsNppAllForYear({ year, date } = {}) {
  return async function dispatchLoadUsNppAllForYear(dispatch, getState) {
    if (!loadUsNppAllForYear._inflight)
      loadUsNppAllForYear._inflight = new Set();
    const key = String(year);
    if (loadUsNppAllForYear._inflight.has(key)) {
      logger.info(
        `[USNPP] loadUsNppAllForYear skipped: in-flight year=${year}`,
      );
      return Promise.resolve();
    }

    let state = getState();
    // If global production and unavailabilities for this year already present, and loads built for this year, skip fetching
    const globalYear = state.usNPPs?.loadedData?.global?.[year] || {};
    const hasProd =
      Array.isArray(globalYear.production) && globalYear.production.length > 0;
    const hasUnav =
      Array.isArray(globalYear.unavailabilities) &&
      globalYear.unavailabilities.length > 0;
    const loads = state.usNPPs?.loads || {};
    const loadsReady = Object.values(loads).some(
      v => v && typeof v.month === 'string' && v.month.startsWith(String(year)),
    );
    if (hasProd && hasUnav && loadsReady) {
      logger.info(
        `[USNPP] loadUsNppAllForYear skipped: already loaded year=${year}`,
      );
      return Promise.resolve();
    }

    // Only mark loading if we actually need to fetch
    if (!hasProd || !hasUnav) dispatch(loadUsNPPsAction());
    loadUsNppAllForYear._inflight.add(key);
    logger.info(`[USNPP] loadUsNppAllForYear starting year=${year}`);

    // Ensure plant list is loaded (for capacity data used in load calculations)
    state = getState();
    const plantCount = Object.keys(state.usNPPs?.data || {}).length;
    if (plantCount === 0) {
      logger.info('[USNPP] Preloading plant list before building loads');
      await dispatch(loadAllUsNPPs());
      state = getState();
      logger.info(
        `[USNPP] Plant list loaded: ${
          Object.keys(state.usNPPs?.data || {}).length
        } items`,
      );
    }

    // Fetch global yearly production (all plants) if missing
    if (!hasProd) {
      try {
        const prod = await getUsNppMonthlyProduction({ year });
        try {
          const rows = prod?.response?.data || prod || [];
          const periods = Array.from(
            new Set((rows || []).map(r => r.period)),
          ).sort();
          const minP = periods[0];
          const maxP = periods[periods.length - 1];
          logger.info(
            `[USNPP] PROD API rows=${rows.length} uniqueMonths=${
              periods.length
            } range=${minP || 'n/a'}..${maxP || 'n/a'}`,
          );
        } catch (e) {}
        await dispatch(receiveUsNPPsAction({ production: prod, year }));
      } catch (e) {
        logger.error('Failed to preload US NPP global production:', e);
      }
    }

    // Fetch global yearly unavailabilities (generator-level) if missing
    if (!hasUnav) {
      try {
        const unav = await getUsNppUnavailabilities({ year });
        await dispatch(receiveUsNPPsAction({ unavailabilities: unav, year }));
      } catch (e) {
        logger.error('Failed to preload US NPP global unavailabilities:', e);
      }
    }

    // After we have production, compute per-plant monthly series and monthly loads
    try {
      const s2 = getState();
      const usNPPsMap = s2.usNPPs?.data || {};
      const plants = Object.values(usNPPsMap);
      const capacityByPlant = plants.reduce((acc, p) => {
        acc[String(p.id)] = Number(p.capacityMW) || 0;
        return acc;
      }, {});

      const prodGlobal =
        s2.usNPPs?.loadedData?.global?.[year]?.production ||
        s2.usNPPs?.production ||
        [];
      const prodRows = prodGlobal?.response?.data || prodGlobal; // handle both shapes
      try {
        const periods = Array.from(
          new Set((prodRows || []).map(r => r.period)),
        ).sort();
        const minP = periods[0];
        const maxP = periods[periods.length - 1];
        logger.info(
          `[USNPP] PROD rows in-store=${(prodRows || []).length} uniqueMonths=${
            periods.length
          } range=${minP || 'n/a'}..${maxP || 'n/a'}`,
        );
      } catch (e) {}
      let byPlantMonth = {};
      if (Array.isArray(prodRows) && prodRows.length > 0) {
        prodRows.forEach(r => {
          const fuel = r.fuel2002 || r.fuelType || r.fuel;
          if (fuel && fuel !== 'NUC') return;
          const primeMover = r.primeMover || r.prime_mover || r['prime-mover'];
          // Accept ST and ALL; prefer ST if both present
          if (primeMover && !['ST', 'ALL'].includes(String(primeMover))) return;
          const plantId = String(
            r.plantCode || r.facility || r.plantId || r.plantid || '',
          );
          if (!plantId) return;
          const period = String(r.period);
          const generation = Number(r.generation) || 0;
          const y = Number(period.slice(0, 4));
          if (y !== Number(year)) return;
          if (!byPlantMonth[plantId]) byPlantMonth[plantId] = {};
          const monthKey = period.slice(0, 7); // YYYY-MM
          if (byPlantMonth[plantId][monthKey] == null) {
            byPlantMonth[plantId][monthKey] = generation;
          } else if (String(primeMover) === 'ST') {
            // Prefer ST value over previously set ALL
            byPlantMonth[plantId][monthKey] = generation;
          }
        });
      }
      // Merge fallback: build per-plant monthly energy from daily unavailability (capacity - outage) * 24
      // and fill only months missing in production rows
      const unavGlobal =
        s2.usNPPs?.loadedData?.global?.[year]?.unavailabilities ||
        s2.usNPPs?.unavailabilities ||
        [];
      if (Array.isArray(unavGlobal) && unavGlobal.length > 0) {
        const dailyByPlantMonth = {};
        unavGlobal.forEach(row => {
          const fid = String(
            row.facility || row.plantCode || row.plantid || '',
          );
          if (!fid) return;
          const dateStr = String(row.period);
          const y = Number(dateStr.slice(0, 4));
          if (y !== Number(year)) return;
          const mKey = dateStr.slice(0, 7);
          const cap = Number(row.capacity) || 0;
          const out = Number(row.outage) || 0;
          const avail = Math.max(0, cap - out);
          const dailyMWh = avail * 24;
          if (!dailyByPlantMonth[fid]) dailyByPlantMonth[fid] = {};
          dailyByPlantMonth[fid][mKey] =
            (dailyByPlantMonth[fid][mKey] || 0) + dailyMWh;
        });
        // merge
        Object.entries(dailyByPlantMonth).forEach(([fid, monthsMap]) => {
          if (!byPlantMonth[fid]) byPlantMonth[fid] = {};
          Object.entries(monthsMap).forEach(([mKey, mVal]) => {
            if (
              byPlantMonth[fid][mKey] == null ||
              byPlantMonth[fid][mKey] === 0
            ) {
              byPlantMonth[fid][mKey] = mVal;
            }
          });
        });
      }

      // Build productionByPlant for this year with padded months 1..lastMonth
      const productionByPlantYear = Object.fromEntries(
        Object.entries(byPlantMonth).map(([plantId, monthsMap]) => {
          const arr = [];
          for (let m = 1; m <= 12; m += 1) {
            const key = `${year}-${String(m).padStart(2, '0')}`;
            arr.push({ month: m, production: Number(monthsMap[key] || 0) });
          }
          return [String(plantId), arr];
        }),
      );
      try {
        const samplePlant = Object.keys(productionByPlantYear)[0];
        if (samplePlant) {
          const sample = productionByPlantYear[samplePlant];
          const nonZero = sample
            .filter(m => Number(m.production) > 0)
            .map(m => m.month);
          logger.info(
            `[USNPP] Sample plant=${samplePlant} months nonZero=${nonZero.join(
              ',',
            ) || 'none'}`,
          );
        }
      } catch (e) {}

      // Build loads map: use the last available month key for each plant
      const loads = Object.fromEntries(
        Object.entries(byPlantMonth).map(([plantId, monthsMap]) => {
          const monthKeys = Object.keys(monthsMap).sort();
          // prefer last non-zero month; else latest key
          const lastNonZero = [...monthKeys]
            .reverse()
            .find(k => Number(monthsMap[k] || 0) > 0);
          const lastMonthKey = lastNonZero || monthKeys[monthKeys.length - 1];
          const monthProdMWh = monthsMap[lastMonthKey] || 0;
          const cap = capacityByPlant[plantId] || 0;
          const totalHoursInMonth = lastMonthKey
            ? moment(lastMonthKey + '-01').daysInMonth() * 24
            : 0;

          // For current month, use elapsed hours for more accurate load calculations
          const now = moment();
          const currentMonth = now.format('YYYY-MM');
          const isCurrentMonth = lastMonthKey === currentMonth;
          const effectiveHours = isCurrentMonth
            ? getElapsedHoursInCurrentMonth(now.year(), now.month() + 1)
            : totalHoursInMonth;

          const monthlyLoadRate =
            cap > 0 && effectiveHours > 0
              ? Math.max(0, Math.min(1, monthProdMWh / (cap * effectiveHours)))
              : 0;

          return [
            String(plantId),
            {
              loadRate: monthlyLoadRate,
              monthlyLoadRate,
              dailyLoadRate: null,
              notif: false, // fill below with unavailabilities
              month: lastMonthKey || null,
              monthProdMWh,
              hoursInMonth: totalHoursInMonth,
              effectiveHours: effectiveHours,
              isCurrentMonth: isCurrentMonth,
            },
          ];
        }),
      );
      logger.info(
        `[USNPP] Built monthly loads for ${
          Object.keys(loads).length
        } plants for year=${year}`,
      );

      // Compute notif flags for the given date (any outage > 0 that day)
      if (date) {
        const dateStr = moment(date).format('YYYY-MM-DD');
        const unavGlobal =
          s2.usNPPs?.loadedData?.global?.[year]?.unavailabilities ||
          s2.usNPPs?.unavailabilities ||
          [];
        if (Array.isArray(unavGlobal)) {
          // aggregate outages by facility for selected date
          const byFacility = {};
          unavGlobal
            .filter(
              it =>
                typeof it.period === 'string' && it.period.startsWith(dateStr),
            )
            .forEach(it => {
              const fid = String(
                it.facility || it.plantCode || it.plantid || '',
              );
              if (!fid) return;
              const out = Number(it.outage) || 0;
              byFacility[fid] = (byFacility[fid] || 0) + out;
            });
          Object.keys(loads).forEach(fid => {
            if (byFacility[fid] > 0) {
              loads[fid].notif = true;
            }
          });
        }
      }

      // Update store with per-plant monthly series and computed loads
      await dispatch(
        receiveUsNPPsAction({
          productionByPlant: productionByPlantYear,
          loads,
          year,
        }),
      );
    } catch (e) {
      logger.error('Failed to build US NPP monthly loads from production:', e);
    }

    // Global outage aggregated series (optional, global chart)
    try {
      const st3 = getState();
      const hasOutage = !!st3.usNPPs?.loadedData?.global?.[year]?.outage;
      if (!hasOutage) {
        await getUsNuclearOutage({ year }).then(data =>
          dispatch(receiveUsNPPsAction({ outage: data, year })),
        );
      }
    } catch (e) {
      logger.error('Failed to preload US NPP global outage:', e);
    }
    loadUsNppAllForYear._inflight.delete(key);
  };
}

const initialState = {
  loading: false,
  loaded: false,
  errors: null,
  data: {},
  // Track loaded data by plantId and year to avoid re-fetching
  loadedData: {
    // structure: { [plantId]: { [year]: { unavailabilities, outage, production } } }
  },
  unavailabilities: null,
  outage: null,
  production: null,
  loads: {},
  snapshot: null,
  productionByPlant: {}, // { [year]: { [plantId]: [{ month, production }] } }
};

function usNPPsReducer(state = initialState, action) {
  const { type } = action;
  switch (type) {
    case US_NPPS_LOAD_ACTION:
      return { ...state, loading: true };
    case US_NPPS_RECEIVE_ACTION:
      // Update loadedData tracking when we successfully receive data
      let newLoadedData = { ...state.loadedData };

      // Handle global data (no plantId)
      if (!action.plantId) {
        if (!newLoadedData.global) {
          newLoadedData.global = {};
        }

        if (action.unavailabilities) {
          newLoadedData.global[action.year] = {
            ...newLoadedData.global[action.year],
            unavailabilities: action.unavailabilities,
          };
        }

        if (action.outage) {
          newLoadedData.global[action.year] = {
            ...newLoadedData.global[action.year],
            outage: action.outage,
          };
        }

        if (action.production) {
          newLoadedData.global[action.year] = {
            ...newLoadedData.global[action.year],
            production: action.production,
          };
        }
        if (action.productionByPlant) {
          newLoadedData.global[action.year] = {
            ...newLoadedData.global[action.year],
            productionByPlant: action.productionByPlant,
          };
        }
      } else {
        // Handle plant-specific data
        const plantKey = action.plantId.toString();
        if (!newLoadedData[plantKey]) {
          newLoadedData[plantKey] = {};
        }

        if (action.unavailabilities) {
          newLoadedData[plantKey][action.year] = {
            ...newLoadedData[plantKey][action.year],
            unavailabilities: action.unavailabilities,
          };
        }

        if (action.outage) {
          newLoadedData[plantKey][action.year] = {
            ...newLoadedData[plantKey][action.year],
            outage: action.outage,
          };
        }

        if (action.production) {
          newLoadedData[plantKey][action.year] = {
            ...newLoadedData[plantKey][action.year],
            production: action.production,
          };
        }
      }

      return {
        ...state,
        loading: false,
        // Only consider the list "loaded" when we actually receive the plant list.
        // Keep previous value for other per-plant/per-year fetches (unavailabilities/outage/production).
        loaded: action.data !== undefined ? !action.errors : state.loaded,
        // Only update global errors when handling the plant list response
        errors:
          action.data !== undefined ? action.errors || null : state.errors,
        data: action.data !== undefined ? action.data : state.data,
        // Keep top-level global series only when action has no plantId
        unavailabilities:
          !action.plantId && action.unavailabilities
            ? action.unavailabilities
            : state.unavailabilities,
        outage: !action.plantId && action.outage ? action.outage : state.outage,
        production:
          !action.plantId && action.production
            ? action.production
            : state.production,
        productionByPlant: action.productionByPlant
          ? {
              ...state.productionByPlant,
              [action.year]: action.productionByPlant,
            }
          : state.productionByPlant,
        loads: action.loads || state.loads,
        snapshot: action.snapshot || state.snapshot,
        loadedData: newLoadedData,
      };
    default:
      return state;
  }
}

export function usNPPsLoadedSelector(state) {
  // Consider US NPPs "loaded" once the list of plants is present,
  // independent of per-plant/per-year fetch errors/state.
  const data = state.usNPPs?.data || {};
  return Object.keys(data).length > 0;
}

// Base selectors
const getUsNPPsData = state => state.usNPPs?.data || {};

// Simple memoized selector for US NPPs
export const usNPPsSelector = createSelector(
  [getUsNPPsData],
  usNPPsData => {
    return values(usNPPsData);
  },
);

// Memoized selector for getting a single US NPP
export const usNPPSelector = createSelector(
  [getUsNPPsData, (state, { usNPPId }) => usNPPId],
  (usNPPsData, usNPPId) => {
    return usNPPsData[usNPPId] || null;
  },
);

export default usNPPsReducer;
